import React, { useEffect, useState } from 'react';

import { Box as AtomsBox, Col } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { Box, PressableBox, Row, Text } from '@opendoor/bricks-next';
import { useObservability } from 'helpers/observability';
import { useBuyerHubFlagStatus } from 'helpers/useBuyerHubFlag';

import type { MenuConfig, NavigationProps } from '../../types';
import {
  MobileMenuAccountItemsLegacy,
  MobileMenuAccountItemsV2,
} from '../mobile/MobileMenuAccountItems';
import { MobileMenuList } from '../mobile/MobileMenuList';
import { MobileMenuSubMenu } from '../mobile/MobileMenuSubMenu';
import { MenuCTA } from './MenuCTA';

import styles from './MenuContent.module.css';

interface MenuContentProps extends NavigationProps, MenuConfig {
  isVisible: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onAnimationComplete: (definition: 'visible' | 'hidden') => void;
  variant?: 'default' | 'glass';
}

export const MenuContent: React.FC<MenuContentProps> = ({
  isVisible,
  containerRef,
  onAnimationComplete,
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  megaNavData,
  variant = 'default',
}) => {
  const { authentication, logout } = useAuth();
  const { trackEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';
  const hubFlagStatus = useBuyerHubFlagStatus();

  // Gate the redesigned account section behind the buyer-hub rollout flag. While
  // the flag is still resolving, render neither variant so its data queries don't
  // fire before we know whether the user is enrolled.
  let accountItems: React.ReactNode = null;
  if (isLoggedIn) {
    if (hubFlagStatus === 'enabled') {
      accountItems = <MobileMenuAccountItemsV2 slug={slug} />;
    } else if (hubFlagStatus === 'disabled') {
      accountItems = <MobileMenuAccountItemsLegacy slug={slug} />;
    }
  }

  const handleSignOut = () => {
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  // Keep the most recently opened submenu mounted so it can slide back out.
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  useEffect(() => {
    if (openSubMenu) {
      setActiveSubMenu(openSubMenu);
    }
  }, [openSubMenu]);

  // Slide the panel in on the frame after mount; slide out is driven by `isVisible`.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  const open = isVisible && entered;
  const showSubMenu = Boolean(megaNavData && openSubMenu);

  const handleTransitionEnd = (event: React.TransitionEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }
    onAnimationComplete(open ? 'visible' : 'hidden');
  };

  const signOutContent = isLoggedIn ? (
    <Row justifyContent="center" py="$4" px="$3">
      <PressableBox
        onPress={handleSignOut}
        aria-label="Sign out"
        analyticsName="cosmos-account-menu-sign-out"
        display="inline-flex"
        alignItems="center"
      >
        <Text tag="span" typography="$bodyMedium" color="$contentTertiary">
          Sign out
        </Text>
      </PressableBox>
    </Row>
  ) : null;

  const menuFooter = (
    <Box pt="$4">
      {signOutContent}
      <MenuCTA ctaLink={ctaLink} ctaTitle={ctaTitle} open={open} />
    </Box>
  );

  const mainMenu = (
    <MobileMenuList
      megaNavData={megaNavData}
      menuPanelNavLinks={menuPanelNavLinks}
      mainNavLinks={mainNavLinks}
      slug={slug}
      onSubMenuOpen={setOpenSubMenu}
      accountItems={accountItems}
      avatarHasTopBorder={hubFlagStatus === 'disabled'}
      footer={menuFooter}
    />
  );

  return (
    <AtomsBox
      ref={containerRef}
      tabIndex={-1}
      className={cn(
        styles.menuContainer,
        'grid transition-transform duration-500 ease-out',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
      data-variant={variant}
      role="dialog"
      aria-labelledby="dialog-main-menu"
      aria-modal="true"
      onTransitionEnd={handleTransitionEnd}
    >
      {/* eslint-disable-next-line @opendoor/no-html-elements -- Atoms has no list primitive; <ul> preserves list semantics */}
      <ul className={cn(styles.menuList, 'relative block')}>
        {megaNavData ? (
          <>
            <Col
              className={cn(
                'absolute inset-0 transition-transform duration-300 ease-in-out',
                showSubMenu ? '-translate-x-full' : 'translate-x-0',
              )}
            >
              {mainMenu}
            </Col>
            {activeSubMenu && (
              <Col
                className={cn(
                  'absolute inset-0 transition-transform duration-300 ease-in-out',
                  showSubMenu ? 'translate-x-0' : 'translate-x-full',
                )}
              >
                <MobileMenuSubMenu
                  megaNavData={megaNavData}
                  openSubMenu={activeSubMenu}
                  onBack={() => setOpenSubMenu(null)}
                  footer={menuFooter}
                />
              </Col>
            )}
          </>
        ) : (
          mainMenu
        )}
      </ul>
    </AtomsBox>
  );
};
